import { Fragment, useRef, useState } from 'react';
import type { ActionOutcome, BoardControl, BoardView, ParticipantPublic } from '@momentum/shared';
import { isMomentumWorld } from './momentumWorld.ts';

export interface BoardPanelProps {
  view: BoardView;
  readOnly: boolean;
  send: (name: string, payload: unknown) => Promise<ActionOutcome>;
  participants: ParticipantPublic[];
  youId: string | null;
  /**
   * The 3D board is presenting the route choice as in-world chevrons, so the duplicate named
   * buttons here are visually hidden. They stay in the DOM and stay operable: the chevrons are a
   * presentation of these controls, not a replacement, and a screen reader or keyboard still
   * reaches the real thing — including the "· longer" hint the chevron cannot carry.
   */
  routeControlsInWorld?: boolean;
}

/**
 * Status/controls panel shared by the 2D and 3D board renderers (`BoardRenderer.tsx` mounts exactly one,
 * regardless of which visual mode is active): `status.title/lines`, `progress` as a definition list, the
 * round roster with completion ticks, the last roll, and `controls[]` rendered as real buttons. Pressing a
 * control sends its named action and shows a reject message inline next to that button; nothing here assumes
 * anything about the outcome beyond what `send` resolves to.
 */
export function BoardPanel({ view, readOnly, send, participants, youId, routeControlsInWorld = false }: BoardPanelProps) {
  const pending = useRef(false);
  const slice = isMomentumWorld(view.graph);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [rejects, setRejects] = useState<Record<string, string>>({});

  function nameOf(participantId: string): string {
    if (participantId === youId) return 'You';
    return participants.find((p) => p.id === participantId)?.displayName ?? participantId;
  }

  async function press(control: BoardControl): Promise<void> {
    if (!control.enabled || pending.current) return;
    pending.current = true;
    setPendingId(control.id);
    const outcome = await send(control.action, control.payload);
    pending.current = false;
    setPendingId(null);
    setRejects((prev) => ({ ...prev, [control.id]: outcome.ok ? '' : outcome.message || outcome.code }));
  }

  return (
    <section className={slice ? "momentum-board-status" : undefined} aria-label="Board status" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4, 16px)' }}>
      <div>
        <h3 hidden={slice} style={{ margin: '0 0 4px' }}>{view.status.title}</h3>
        <ul style={{ margin: 0, paddingLeft: 20 }}>
          {view.status.lines.map((line, index) => (
            <li key={index}>{line}</li>
          ))}
        </ul>
      </div>

      {!slice && Object.keys(view.progress).length > 0 && (
        <dl style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '2px 12px', margin: 0 }}>
          {Object.entries(view.progress).map(([key, value]) => (
            <Fragment key={key}>
              <dt style={{ fontWeight: 600 }}>{key}</dt>
              <dd style={{ margin: 0 }}>{value}</dd>
            </Fragment>
          ))}
        </dl>
      )}

      {view.round && (
        <div>
          <h4 style={{ margin: '0 0 4px' }}>
            Round {view.round.number}
            {view.round.launched ? ' — launched' : ''}
          </h4>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {view.round.roster.map((participantId) => {
              const done = view.round!.completed.includes(participantId);
              return (
                <li key={participantId}>
                  {nameOf(participantId)} — {done ? 'done ✓' : 'waiting'}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {!slice && view.lastRoll && (
        <p style={{ margin: 0 }}>
          {nameOf(view.lastRoll.participantId)} rolled {view.lastRoll.value}.
        </p>
      )}

      {!readOnly && view.controls.length > 0 && (
        <div className={slice ? "momentum-board-actions" : undefined} role="group" aria-label="Board actions" style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2, 8px)' }}>
          {view.controls.map((control) => (
            <div key={control.id} className={routeControlsInWorld && control.action === 'route' ? 'visually-hidden' : undefined} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <button
                type="button"
                disabled={!control.enabled || pendingId !== null}
                onClick={() => void press(control)}
                title={control.hint ?? undefined}
                style={{
                  minHeight: 'var(--touch-target, 44px)',
                  minWidth: 'var(--touch-target, 44px)',
                  padding: '8px 12px',
                  borderRadius: 'var(--radius-control, 12px)',
                  ...(slice ? { background: '#2365ef', color: 'white', border: 0, fontWeight: 600, padding: '12px 24px' } : {}),
                }}
              >
                {control.label}
              </button>
              {rejects[control.id] ? (
                <p role="alert" style={{ color: 'var(--danger, #d64545)', margin: 0, fontSize: '0.85em' }}>
                  {rejects[control.id]}
                </p>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
